import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";

export default function Clients() {
  const [clients, setClients] = useState([]);
  const [query, setQuery] = useState("");

  useEffect(() => { api.get("/clients").then((r) => setClients(r.data || [])); }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return clients;
    return clients.filter((c) =>
      (c.full_name || "").toLowerCase().includes(q) ||
      (c.email || "").toLowerCase().includes(q) ||
      (c.id_number || "").toLowerCase().includes(q),
    );
  }, [clients, query]);

  return (
    <div className="px-6 md:px-10 py-10 max-w-[1400px] mx-auto" data-testid="admin-clients">
      <div className="mb-8 flex items-end justify-between gap-6 flex-wrap">
        <div>
          <div className="text-overline text-zinc-500">Clients</div>
          <h1 className="font-serif text-4xl lg:text-5xl font-medium tracking-tight mt-2">
            Client roll
          </h1>
          <p className="text-sm text-zinc-500 mt-2">
            One row per unique person. Each client may hold multiple matters across
            services.
          </p>
        </div>
        <div className="flex items-center gap-3 text-sm">
          <span className="text-zinc-500 whitespace-nowrap">{filtered.length} of {clients.length} clients</span>
          <input
            data-testid="clients-search"
            type="search"
            placeholder="Search name, email, ID…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="border border-zinc-300 px-3 py-2 w-72 max-w-full text-sm focus:outline-none focus:border-zinc-900"
          />
        </div>
      </div>

      <div className="bg-white border border-zinc-200 overflow-x-auto" data-testid="clients-table-wrap">
        {clients.length === 0 ? (
          <div className="p-10 text-center text-zinc-500" data-testid="clients-empty">
            No clients yet.
          </div>
        ) : (
          <table className="w-full text-sm min-w-[960px]">
            <thead className="border-b border-zinc-200 bg-zinc-50">
              <tr className="text-left">
                <Th className="w-[30%]">Client</Th>
                <Th className="w-[22%]">Contact</Th>
                <Th className="w-[60px]">Matters</Th>
                <Th className="w-[20%]">Services</Th>
                <Th className="w-[14%]">Status</Th>
                <Th className="w-[10%]">Last activity</Th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => (
                <tr
                  key={c.id}
                  className={`border-b border-zinc-100 hover:bg-zinc-50 cursor-pointer transition-colors ${
                    c.needs_attention ? "bg-amber-50/40" : ""
                  }`}
                  onClick={() => { window.location.href = `/admin/clients/${c.id}`; }}
                  data-testid={`client-row-${c.id}`}
                >
                  <td className="px-5 py-4 align-top">
                    <div className="flex items-center gap-2">
                      {c.needs_attention && (
                        <span
                          className="h-2 w-2 rounded-full bg-amber-500 flex-shrink-0"
                          title="Needs attention"
                          data-testid={`client-needs-attention-${c.id}`}
                        />
                      )}
                      <Link
                        to={`/admin/clients/${c.id}`}
                        className="font-medium text-zinc-900 hover:underline break-words"
                        data-testid={`client-name-link-${c.id}`}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {c.full_name || "—"}
                      </Link>
                    </div>
                    {c.id_number && (
                      <div className="font-mono text-[11px] text-zinc-500 mt-1">
                        ID {c.id_number}
                      </div>
                    )}
                  </td>
                  <td className="px-5 py-4 align-top">
                    <div className="text-zinc-900 break-all">{c.email || "—"}</div>
                    {c.phone && <div className="text-zinc-500 text-xs mt-1 whitespace-nowrap">{c.phone}</div>}
                  </td>
                  <td className="px-5 py-4 align-top">
                    <span
                      className={`inline-flex items-center justify-center min-w-[1.75rem] h-6 px-2 rounded-full text-xs font-medium ${
                        (c.matter_count || 0) === 0
                          ? "bg-zinc-100 text-zinc-500"
                          : "bg-zinc-900 text-white"
                      }`}
                      data-testid={`client-matter-count-${c.id}`}
                    >
                      {c.matter_count || 0}
                    </span>
                  </td>
                  <td className="px-5 py-4 align-top">
                    {(c.services && c.services.length) ? (
                      <div className="flex flex-wrap gap-1">
                        {c.services.map((s) => (
                          <span
                            key={s}
                            className="px-2 py-0.5 text-[11px] tracking-wide bg-zinc-100 text-zinc-700 rounded-sm whitespace-nowrap"
                          >
                            {s}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="text-zinc-400">—</span>
                    )}
                  </td>
                  <td className="px-5 py-4 align-top">
                    {c.needs_attention ? (
                      <span
                        className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium bg-amber-100 text-amber-900 rounded-sm whitespace-nowrap"
                        data-testid={`client-attention-badge-${c.id}`}
                      >
                        Needs attention
                        {c.outstanding_count > 1 && (
                          <span className="text-amber-700">· {c.outstanding_count}</span>
                        )}
                      </span>
                    ) : (
                      <span className="text-xs text-zinc-400">On track</span>
                    )}
                  </td>
                  <td className="px-5 py-4 align-top text-zinc-500 text-xs whitespace-nowrap">
                    {c.last_activity_at
                      ? new Date(c.last_activity_at).toLocaleDateString()
                      : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

const Th = ({ children, className = "" }) => (
  <th className={`px-5 py-3 text-overline text-zinc-500 font-normal ${className}`}>{children}</th>
);
